import React, {
  InputHTMLAttributes,
  forwardRef,
  useState,
  HTMLProps,
  useCallback,
  ChangeEvent,
  useEffect,
} from 'react'
import styled, { useTheme } from 'styled-components'
import { animated, useSpring } from '@react-spring/web'

const BasicInput = styled.input`
  font-family: 'Matter';
  font-style: normal;
  width: 100%;
  font-weight: 700;
  font-size: 36px;
  line-height: 130%;
  text-align: center;
  background: #595a63;
  color: ${(props) => props.theme.white};

  outline: none;
  border: none;

  -webkit-appearance: textfield;
  ::-webkit-search-decoration {
    -webkit-appearance: none;
  }
  [type='number'] {
    -moz-appearance: textfield;
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  /* identical to box height, or 47px */

  /* display: flex;
align-items: center;
text-align: center; */
`

const DrawtoolStyledInput = styled(BasicInput)`
  background: #eaebf1;
  /* Basic/Black */
  width: 100%;
  height: 70px;
  border: 3px solid #080a0c;
  border-radius: 16px;

  padding: 24px 32px 24px 14px;
  font-family: 'Matter';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 130%;
  /* or 23px */
  text-align: left;

  /* Basic/Black */
  color: #080a0c;
`

const StyledInput = styled.input`
  width: 256px;

  text-align: center;

  outline: none;

  height: 80px;

  background: ${(props) => props.theme.background};
  box-shadow: inset 0px 1px 2px rgba(34, 31, 39, 0.24);
  border-radius: 16px;

  border: none;
  font-style: normal;
  font-weight: 900;
  font-size: 48px;
  line-height: 100%;
  color: ${(props) => props.theme.black};

  -webkit-appearance: textfield;
  ::-webkit-search-decoration {
    -webkit-appearance: none;
  }
  [type='number'] {
    -moz-appearance: textfield;
  }
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  /* ::placeholder {
      color: ${({ theme }) => theme.secondary1};
    } */
`

// :hover:not(:focus-within) {
//   background-color: ${({ theme }) => theme.onHover(theme.container)};
//   border-color: ${({ theme }) => theme.onHover(theme.container)};
// }
// :focus-within {
//   border-color: ${({ theme }) => theme.active};
// }

const AnimatedStyledInput = animated(StyledInput)

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  hasError?: boolean
  hasSuccess?: boolean
  isDisabled?: boolean
  viewOnly?: boolean
  style?: React.CSSProperties
}

const Input = forwardRef<HTMLInputElement, InputProps>(({ ...props }, ref) => {
  const [isActive, setIsActive] = useState<boolean>(false)

  const theme = useTheme()

  const boxShadowUnfocused = `0px 0px 0px rgba(0, 0, 0, 0.06), 0px 0px 0px rgba(0, 0, 0, 0.05), inset 0px 1px 2px rgba(34, 31, 39, 0.24)`
  const boxShadowFocused = `0px 2px 5px rgba(0, 0, 0, 0.06), 0px 4px 12px rgba(0, 0, 0, 0.05), inset 0px 0px 0px rgba(34, 31, 39, 0.24)`
  let background = isActive ? theme.white : theme.background

  let boxShadow = isActive ? boxShadowFocused : boxShadowUnfocused

  const activeBorder = '2px solid #8247E5'
  const inactiveBorder = '2px solid transparent'
  let border = isActive ? activeBorder : inactiveBorder

  let textColor = theme.black

  if (props.isDisabled) {
    background = theme.background
    textColor = theme.lightGray
    boxShadow = boxShadowUnfocused
    border = inactiveBorder
  }

  if (props.hasError) {
    border = `2px solid ${theme.red1}`
    background = theme.white
  }

  if (props.hasSuccess) {
    border = `2px solid #34CC34`
    background = theme.white
  }

  const spring = useSpring({
    background,
    boxShadow,
    border,
    color: textColor,
  })

  return (
    <div>
      <AnimatedStyledInput
        {...props}
        style={{
          ...spring,
          ...props.style,
        }}
        ref={ref ?? null}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        // Do something with the extra props
      />
    </div>
  )
})

Input.displayName = 'PrimaryInput'

export interface NumericInputProps
  extends Omit<HTMLProps<HTMLInputElement>, 'onChange' | 'as' | 'value'> {
  value: string
  onChange: (input: string) => void
}

export interface EnforcedNumericInputProps extends NumericInputProps {
  // Validates nextUserInput; returns stringified value, or null if invalid
  enforcer: (nextUserInput: string) => string | null
}

export const NumericInput = forwardRef<HTMLInputElement, EnforcedNumericInputProps>(
  function NumericInput(
    { value, onChange, enforcer, pattern, ...props }: EnforcedNumericInputProps,
    ref,
  ) {
    const validateChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const nextInput = enforcer(event.target.value.replace(/,/g, '.'))?.replace(/^0+$/, '0')
        if (nextInput !== undefined) {
          onChange(nextInput)
        }
      },
      [enforcer, onChange],
    )

    return (
      <Input
        value={value}
        onChange={validateChange}
        // universal input options
        inputMode="decimal"
        autoComplete="off"
        autoCorrect="off"
        // text-specific options
        type="text"
        pattern={pattern}
        placeholder={props.placeholder || '0'}
        minLength={1}
        maxLength={79}
        spellCheck="false"
        ref={ref as any}
        {...props}
      />
    )
  },
)

const integerRegexp = /^\d*$/
const integerEnforcer = (nextUserInput: string) => {
  if (nextUserInput === '' || integerRegexp.test(nextUserInput)) {
    const nextInput = parseInt(nextUserInput)
    return isNaN(nextInput) ? '' : nextInput.toString()
  }
  return null
}

export const IntegerInput = forwardRef(function IntegerInput(props: NumericInputProps, ref) {
  return <NumericInput pattern="^[0-9]*$" enforcer={integerEnforcer} ref={ref as any} {...props} />
})

const decimalRegexp = /^\d*(?:[.])?\d*$/
const decimalEnforcer = (nextUserInput: string) => {
  if (nextUserInput === '') {
    return ''
  } else if (nextUserInput === '.') {
    return '0.'
  } else if (decimalRegexp.test(nextUserInput)) {
    return nextUserInput
  }
  return null
}

export const DecimalInput = forwardRef(function DecimalInput(props: NumericInputProps, ref) {
  return (
    <NumericInput
      pattern="^[0-9]*[.,]?[0-9]*$"
      enforcer={decimalEnforcer}
      ref={ref as any}
      {...props}
    />
  )
})

// const StyledInput = styled.input<InputProps>`
//   font-family: GT Walsheim Pro;
//   font-style: normal;
//   font-weight: normal;
//   font-size: 32px;
//   line-height: 110%;
//   /* or 35px */

//   /* black */

//   color: #090D14;

//   height: 80px;
//   width: 100%;
//   background: ${(props) => (props.surroundingBgColor === 'dark' ? '#ffffff' : '#fffff')};
//   box-sizing: border-box;
//   border: 2px solid
//     ${(props) =>
//       props.hasError ? '#FF656D' : props.hasSuccess ? figmaColors.neonGreen : '#DFE3EF'};

//   /* new */
//   /* border: 2px solid #090D14; */
//   box-shadow: 0px 6px 40px rgba(0, 0, 0, 0.04), 0px 3px 6px rgba(0, 0, 0, 0.02);
//   border-radius: 20px;

//   /* end new */

//   /* font-family: 'Gilroy'; */
//   /* font-weight: 400; */
//   /* font-size: 32px; */
//   /* line-height: 110%; */
//   /* color: #0e103c; */
//   padding-left: 24px;
//   padding-right: 24px;
//   outline: none;
//   will-change: border, box-shadow, opacity;
//   transition: border, 0.15s ease-in, box-shadow, 0.15s ease-out;
//   :hover {
//     border: 2px solid
//       ${(props) =>
//         props.hasError ? '#FF656D' : props.hasSuccess ? figmaColors.neonGreen : '#62677A'};
//     box-shadow: 0px 6px 40px rgba(0, 0, 0, 0.04), 0px 3px 6px rgba(0, 0, 0, 0.02);
//   }
//   :focus {
//     border: 2px solid
//       ${(props) =>
//         props.hasError ? '#FF656D' : props.hasSuccess ? figmaColors.neonGreen : '#090D14'};
//     box-shadow: 0px 6px 40px rgba(0, 0, 0, 0.04), 0px 3px 6px rgba(0, 0, 0, 0.02);
//   }
//   /* ::placeholder {
//     color: #62677a;
//   } */
// `

const InlineEditableStyledInput = styled(BasicInput)<{ isDarkMode: boolean }>`
  background: #ffffff;
  border: 3px solid #080a0c;
  border-radius: 40px;
  color: #080a0c;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 130%;
  text-align: left;
  padding: 12px 12px 12px 20px;

  ::placeholder {
    color: ${(props) => (props.isDarkMode ? 'white' : 'black')}
    opacity: 0.8;
  }
`

export interface InlineEditableInputProps extends InputHTMLAttributes<HTMLInputElement> {
  // hasError?: boolean
  // hasSuccess?: boolean
  // isDisabled?: boolean
  // viewOnly?: boolean
  // style?: React.CSSProperties
  value: string
  // Callback when input confirms. Returns whether it should revert back to original title from parent
  onUpdatedValue: (s: string) => Promise<null | 'revert'>
  isDarkMode: boolean
}

const InlineEditableInput = forwardRef<HTMLInputElement, InlineEditableInputProps>(
  ({ ...props }, ref) => {
    const parentValue = props.value?.toString() ?? ''
    // new
    const [editingValue, setEditingValue] = useState<string>(parentValue ?? '')
    const [focused, setFocused] = useState<boolean>(false)

    // Synchornize the value (e.g. initial value loads async/after mount)
    useEffect(() => {
      setEditingValue(props.value?.toString() ?? '')
    }, [props.value])

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) =>
      setEditingValue(event.target.value)

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === 'Escape') {
        ;(event.target as HTMLElement)?.blur()
      }
      event.stopPropagation()
    }

    const onBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false)

      if (event.target.value.trim() === '') {
        setEditingValue(parentValue)
      } else {
        const maybeShouldRevert = await props.onUpdatedValue(event.target.value)
        if (maybeShouldRevert === 'revert') {
          setEditingValue(parentValue)
        }
      }
    }

    const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true)
    }
    // end new

    // const theme = useTheme()

    // const boxShadowUnfocused = `0px 0px 0px rgba(0, 0, 0, 0.06), 0px 0px 0px rgba(0, 0, 0, 0.05), inset 0px 1px 2px rgba(34, 31, 39, 0.24)`
    // const boxShadowFocused = `0px 2px 5px rgba(0, 0, 0, 0.06), 0px 4px 12px rgba(0, 0, 0, 0.05), inset 0px 0px 0px rgba(34, 31, 39, 0.24)`
    // let background = isActive ? theme.white : theme.background

    // let boxShadow = isActive ? boxShadowFocused : boxShadowUnfocused

    // const activeBorder = '2px solid #8247E5'
    // const inactiveBorder = '2px solid transparent'
    // let border = isActive ? activeBorder : inactiveBorder

    // let textColor = theme.black

    // if (props.isDisabled) {
    //   background = theme.background
    //   textColor = theme.lightGray
    //   boxShadow = boxShadowUnfocused
    //   border = inactiveBorder
    // }

    // if (props.hasError) {
    //   border = `2px solid ${theme.red1}`
    //   background = theme.white
    // }

    // const spring = useSpring({
    //   background,
    //   boxShadow,
    //   border,
    //   color: textColor,
    // })

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <InlineEditableStyledInput
          {...props}
          style={{
            // ...spring,
            ...props.style,
            fontWeight: 600,
            fontSize: 18,
          }}
          ref={ref ?? null}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          value={editingValue}
          placeholder={'Untitled Artwork'}
        />
        <EditIcon />
      </div>
    )
  },
)

InlineEditableInput.displayName = 'InlineEditableInput'

export { Input, BasicInput, DrawtoolStyledInput, InlineEditableInput }

type IconProps = React.SVGAttributes<SVGElement> & {
  isFocused: boolean
}

const EditIcon = () => {
  return (
    <div style={{ position: 'absolute', right: 6, height: 32, width: 32, pointerEvents: 'none' }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        fill="none"
        viewBox="0 0 32 32"
      >
        <rect width="32" height="32" fill="#080A0C" rx="16"></rect>
        <path
          fill="#fff"
          fillRule="evenodd"
          d="M20.7 8.3l3 3c.4.4.4 1 0 1.4l-1.8 1.8-4.4-4.4 1.8-1.8c.4-.4 1-.4 1.4 0zm-12.4 11l7.8-7.8 4.4 4.4-7.8 7.8c-.2.2-.4.3-.7.3H9c-.6 0-1-.4-1-1v-3c0-.3.1-.5.3-.7z"
          clipRule="evenodd"
        ></path>
      </svg>
    </div>
  )
}

const PencilIcon = (props: IconProps) => {
  const { fill, ...rest } = props
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="none"
      viewBox="0 0 20 20"
      {...rest}
    >
      <path
        fill={fill}
        fillRule="evenodd"
        d="M14.7 2.3l3 3c.4.4.4 1 0 1.4l-1.8 1.8-4.4-4.4 1.8-1.8c.4-.4 1-.4 1.4 0zm-12.4 11l7.8-7.8 4.4 4.4-7.8 7.8c-.2.2-.4.3-.7.3H3c-.6 0-1-.4-1-1v-3c0-.3.1-.5.3-.7z"
        clipRule="evenodd"
      ></path>
    </svg>
  )
}
